import { z } from 'zod'

// SEN-633 — generated from monad/poiesis/contract.py (do not hand-edit the
// reduce contract here; run `npm run gen:contract`).
export type { ReduceRequest, Reducer, IdentityReducer, ScalarReducer, GroupByReducer, TimeBucketReducer } from './contract.generated'
export type { ReducedFrame } from './contract.frame.generated'

// Shapes verified live against the running monad API (2026-06-02).
export const RecordResponse = z.object({
  record_id: z.string(),
  type_id: z.string(),
  created_at: z.string().optional(),
  values: z.record(z.string(), z.unknown()),
  /** SEN-1731 cold-read round 3 major #1: the window `calculated` attributes
   *  were actually resolved over — null when no calculated attribute was
   *  requested at all. Serialized by monad/api/records.py (both
   *  get_record_composite and get_record). This schema had NO `.passthrough()`,
   *  so before this field was declared explicitly, a plain z.object() parse
   *  silently STRIPPED it — unreadable, not merely unread. */
  calculated_period: z.object({
    period_start: z.string(),
    period_end: z.string(),
  }).nullable().optional(),
})
export type RecordResponse = z.infer<typeof RecordResponse>

export const Attribute = z
  .object({
    attribute_id: z.string(),
    slug: z.string(),
    name: z.string(),
    type: z.string(),
    config: z.record(z.string(), z.unknown()).nullish(),
    is_system: z.boolean().optional(),
    /** True when the attribute must have a value on record creation. Serialized
     *  by monad/api/attributes.py:166. Default false when absent. */
    is_required: z.boolean().optional(),
    // Task 8: canonical editability flag emitted by the backend (Task 7).
    // Supersedes the client-side is_system/is_reverse derivation.
    read_only: z.boolean().optional(),
    /** Phase 1: render dispatch hint for generic read components.
     *  String family: "plain" | "mono" | "link".
     *  Number family: "unit" | "percentage".
     *  null/absent: use existing widget registration or unsupported fallback. */
    render_hint: z.string().nullish(),
    /** Phase 1: ordered list of FilterOp strings this type supports.
     *  Drives isFilterable on the frontend. null = not filterable. */
    filter_operators: z.array(z.string()).nullish(),
    /** Phase 1: sort semantics for the column sorter.
     *  "numeric" | "text" | "timestamp" | "none". */
    sort_family: z.enum(['numeric', 'text', 'timestamp', 'none']).nullish(),
    /** SEN-1766: the kind's DECLARED STORABLE column presentation variants
     *  (date → ["weeks_since"] — the identity/default presentation is
     *  deliberately excluded, fix round 1 R1: naming it explicitly would
     *  alias the plain entry). Serialized by monad/api/serialization.py
     *  enrich_render_hint_and_filter. Empty/absent = one presentation.
     *  NOTE: this is narrower than the widget's own display list
     *  (poiesis/src/widgets/cells/date.tsx), which still offers the
     *  identity variant in its menu — the two lists differ by exactly
     *  that one entry, on purpose. */
    formats: z.array(z.string()).nullish(),
    /** SEN-1731 cold-read major #2: true iff this attribute's definition tree
     *  reaches a `measurement` source (transitively through METRIC refs) —
     *  i.e. a period binding means something for it. NOT the same as
     *  `type === 'calculated'`: roi_net_return/total_savings_initiatives_kwh
     *  are pure cell arithmetic with no measurement leg, so they carry
     *  `windowed: false` (cold-read round 3 minor #3: simple_payback_years is
     *  NOT a safe non-windowed example here — it transitively reaches a real
     *  measurement leaf and IS windowed). Serialized by
     *  monad/api/serialization.py enrich_windowed. Always present for a
     *  calculated attribute; absent/false for every other type. */
    windowed: z.boolean().optional(),
    /** SEN-316: a calculated attribute's formula in plain English (served by
     *  monad/metrics/describe.py; absent for every other type). */
    formula_readback: z.string().optional(),
    /** SEN-437: the attribute's declared default, applied by the server when a
     *  record is created without it: `{value}` (a reference's `{match}` default
     *  is served with the `value` it resolves to) or `{system: 'created_at'}`. */
    default_value: z.record(z.string(), z.unknown()).nullish(),
  })
  .passthrough()
export type Attribute = z.infer<typeof Attribute>
export const AttributesResponse = z.array(Attribute)

export const TypeResponse = z
  .object({
    type_id: z.string(),
    slug: z.string(),
    name: z.string(),
    display: z
      .object({
        icon: z.string().nullish(),
        color: z.string().nullish(),
        identity: z.object({
          icon: z.record(z.string(), z.unknown()).optional(),
          color: z.record(z.string(), z.unknown()).optional(),
        }).optional(),
      })
      // The rest of the type's display metadata rides along (RecordPage reads
      // `highlights`, the record page's curated fields; the parse used to
      // strip it, so a type's declared highlights never reached the page).
      .passthrough()
      .nullish(),
  })
  .passthrough()
export type TypeResponse = z.infer<typeof TypeResponse>

// SEN-1766: a column entry stays an ARRAY OF HOPS; its presentation variant
// (`format`) and its server-computed identity (`column_id`) ride the LEAF hop
// — the shape `sub_field` already uses on the filter/sort wire. Both are
// optional so an older backend's views still parse.
const Hop = z.object({
  type_id: z.string(),
  attribute_id: z.string(),
  sub_field: z.string().optional(),
  format: z.string().nullish(),
  column_id: z.string().optional(),
})
export const View = z
  .object({
    view_id: z.string(),
    view_type: z.string(),
    title: z.string(),
    /** RAW stored state: empty means "the user never customised this view".
     *  Written back by saveView; never read as the column list (SEN-1755). */
    column_paths: z.array(z.array(Hop)),
    /** RESOLVED column hop-paths — what this view actually shows. Derived
     *  server-side (monad/views_fabricator.py::resolve_view_columns) when
     *  column_paths is empty, echoed verbatim when it is not. THE seam: the
     *  client never re-derives (tests/test_no_special_casing.py::
     *  test_no_client_side_column_path_derivation). Empty is a real answer —
     *  a type with zero visible attributes — never a loading state.
     *  Defaulted so an older backend's views still parse. */
    columns: z.array(z.array(Hop)).default([]),
    column_widths: z.record(z.string(), z.number()).default({}),
    /** SEN-327: the view's filter names a field this reader cannot read, so
     *  the server withheld it whole (pruning a term would change which
     *  records match). The GUI says so; it never shows a different filter. */
    filter_withheld: z.boolean().optional(),
    /** Work screen (2026-10-21): 'saved' = a stored view (a person's save or
     *  a pack's declared view); 'fabricated' = a default made up on read.
     *  A host lists a type's saved views without naming any. */
    origin: z.enum(['saved', 'fabricated']).optional(),
    /** The view's own filter, in the records/query contract grammar
     *  ({attribute, op, value} | {and|or: [...]}). A value may name the
     *  caller ({"$principal": "person"}); records/query binds it. */
    filter: z.unknown().optional(),
    /** The kind's config: option values + structural bindings
     *  (`roleNames`, e.g. the table's `group` role). */
    config: z.record(z.string(), z.unknown()).optional(),
  })
  .passthrough()
export type View = z.infer<typeof View>
export const ViewsResponse = z.array(View)

export const MrlResponse = z
  .object({
    result_snapshot_id: z.string(),
    total_count: z.number(),
    record_ids: z.array(z.string()),
    /** Map of record_id → display name (kenning). Present when the type has a
     *  kenning_path configured. Used by FilterValue's entity-reference selector. */
    record_kennings: z.record(z.string(), z.string()).optional(),
  })
  .passthrough()
export type MrlResponse = z.infer<typeof MrlResponse>

// ---------------------------------------------------------------------------
// MRL filter contract — the SAME type the builder edits and the MRL sends.
// Mirrors monad/query.py FilterExpression (the FE/BE narrow-waist boundary).
// Outbound-only (built here, sent to the MRL, never parsed from a response),
// so these are plain interfaces with no Zod validator — unlike the response
// schemas above. FilterOp is the single source for the operator union: the
// widget registry's FilterOperator.op imports it.
// ---------------------------------------------------------------------------
export type FilterOp = 'eq' | 'neq' | 'contains' | 'gt' | 'lt' | 'gte' | 'lte' | 'empty' | 'non-empty'

export interface FilterConstraint {
  field: string
  operator: FilterOp
  value?: unknown
  // SEN-671 — the leaf hop's sub_field (composite drill-in); rides the same hop.
  sub_field?: string
}
export interface FilterClause {
  path: { type_id: string; attribute_id: string }[]
  // The MRL + backend support 'must-not' (NOT EXISTS), but the builder only ever
  // emits 'must' today; 'must-not' is reserved for a future per-condition
  // "does not match" toggle. Kept in the union to mirror the backend contract.
  mode: 'must' | 'must-not'
  constraints: FilterConstraint[]
}
export interface FilterExpr {
  and?: FilterExpr[]
  or?: FilterExpr[]
  // 'not' mirrors the backend's NOT_ node; compile() doesn't emit it yet — it's
  // reserved for a future group-level negation ("none of") control.
  not?: FilterExpr
  clause?: FilterClause
}

/** Wire shape for one sort key — mirrors backend monad/query.py SortKey.
 *  The aggregate field is omitted from the FE surface for now (always unset). */
export interface SortKeyWire {
  path: { type_id: string; attribute_id: string }[]
  direction: 'asc' | 'desc'
  // SEN-671 — the leaf hop's sub_field (composite drill-in); rides the same hop.
  sub_field?: string
}

// ---------------------------------------------------------------------------
// Timeline — activity stream per record (monad/timeline.py + monad/api/timeline.py)
// Route: GET /v1/tenants/{slug}/records/{record_id}/timeline
// Response shape: { events: [...], count: N }  (api/timeline.py:57)
//
// Actual event_types from the UNION ALL SQL:
//   cell_set, record_created, record_archived,
//   verb_proposed, verb_decided, comment_added, comment_resolved
//
// Actor shape (monad/actor.py): { type: ActorType, id: string } | null
// t: ISO string or null (record_archived can have null actor; comment_resolved
//    can have null t if resolved_at not set)
// ---------------------------------------------------------------------------
const TimelineActor = z
  .object({ type: z.string(), id: z.string() })
  .nullable()

export const TimelineEvent = z.object({
  event_type: z.string(),
  t: z.string().nullable(),
  actor: TimelineActor,
  /** SEN-208: who the actor is, resolved by the server (member name or
   *  email, an agent/automation/connection's name). Absent when unresolved. */
  actor_display: z.string().nullish(),
  payload: z.record(z.string(), z.unknown()).nullable(),
})
export type TimelineEvent = z.infer<typeof TimelineEvent>

export const TimelineResponse = z.object({
  events: z.array(TimelineEvent),
  count: z.number(),
})
export type TimelineResponse = z.infer<typeof TimelineResponse>

/** Single chip: kenning + conventional icon/colour siblings.
 *  Mirrors the shape resolve_chips_batch returns (monad/store/display.py).
 *  icon/color are null when the target type has no such text attributes or
 *  the record carries no values for them. */
export const Chip = z.object({
  kenning: z.string(),
  icon: z.string().nullable(),
  color: z.string().nullable(),
})
export type Chip = z.infer<typeof Chip>

/** Response shape for POST /v1/tenants/{slug}/records/chips.
 *  chips is keyed by record_id string (serialised UUID). */
export const ChipsResponse = z.object({
  chips: z.record(z.string(), Chip),
  references: z.array(z.object({ record_id: z.string(), chip: Chip }).nullable()).optional(),
})
export type ChipsResponse = z.infer<typeof ChipsResponse>
export interface ChipReferenceLookup {
  target_type: string
  key: string
  value: string
}

/** The generic view-write patch — mirrors backend monad/api/views.py ViewPatch.
 *  All fields optional; only provided keys are written (presence-based). */
export interface ViewPatch {
  column_paths?: { type_id: string; attribute_id: string }[][]
  column_widths?: Record<string, number>
  sort_paths?: { type_id: string; attribute_id: string }[][]
  filter_expr?: FilterExpr
  title?: string
  /** SEN-1424: renderer config (e.g. calendar `scale`) — merged (jsonb ||)
   *  into the stored view config, never a full replace, so a scale toggle
   *  layers over the fabricated roleNames/measureQuantity.
   *  SEN-1431 (ADR-0036): the server validates option values against the
   *  kind's declared option schema (422 on drift). */
  config?: Record<string, unknown>
}

// Daimon wire contracts are generated from the served Python models.
export type { DaimonInvoke, PoiesisContext, ActionMode as DaimonActionMode } from './v1/daimon_invoke.gen'
export type { DaimonOutcome } from './v1/daimon_invoke_success.gen'
export type { DaimonCapability } from './v1/daimon_capability_success.gen'
export type { DaimonInvocation } from './v1/daimon_invocation_success.gen'
export type { ProposedChangeData } from './v1/proposed_changes_success.gen'
